import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Typography, Card, Radio, Upload, Space, Tooltip, Row, Col, Tag, Select, Divider, Menu, Rate, Pagination } from 'antd';
import { UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';

const { TextArea, Search } = Input;
const { Title } = Typography;




const AgentCreate = ({ user }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [templateType, setTemplateType] = useState(null);
    const [uploadedFilesByField, setUploadedFilesByField] = useState({});
    const [uploadedKnowledgeIds, setUploadedKnowledgeIds] = useState([]);
    const navigate = useNavigate();  // useNavigate 훅 추가
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        // 템플릿이 선택되면 해당 템플릿의 upload 필드들을 초기화
        if (templateType) {
            const selectedTemplate = templates.find(t => t.value === templateType);
            if (selectedTemplate) {
                const uploadFields = {};
                selectedTemplate.required_fields.fields
                    .filter(field => field.type === 'upload')
                    .forEach(field => {
                        uploadFields[field.name] = [];
                    });

                setUploadedFilesByField(uploadFields);
                setUploadedKnowledgeIds([]);
            }
        }
    }, [templateType, templates]);


    // 템플릿 데이터 불러오기
    useEffect(() => {
        const fetchTemplates = async () => {
            const { data, error } = await supabase
                .from('agent_templates')
                .select('*')
                .order('usage_count', { ascending: false });

            if (error) {
                message.error('템플릿을 불러오는데 실패했습니다');
                return;
            }

            setTemplates(data);
        };

        fetchTemplates();
    }, []);



    // 동적 폼 생성 컴포넌트
    const DynamicForm = ({ template }) => {
        if (!template) return null;

        return (
            <Form form={form} layout="vertical" onFinish={onFinish}
                initialValues={{
                    name: template.default_name,
                    description: template.default_description
                }}>
                <Form.Item name="name" label="Assistant Name" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="description" label="Assistant Description">
                    <TextArea rows={3} />
                </Form.Item>

                {template.required_fields?.fields?.map(field => {
                    switch (field.type) {
                        case 'input':
                            return (
                                <Form.Item
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    rules={[{ required: field.required }]}
                                >
                                    <Input />
                                </Form.Item>
                            );
                        case 'textarea':
                            return (
                                <Form.Item
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    rules={[{ required: field.required }]}
                                >
                                    <TextArea rows={4} />
                                </Form.Item>
                            );
                        case 'select':
                            return (
                                <Form.Item
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    rules={[{ required: field.required }]}
                                >
                                    <Select options={field.options} />
                                </Form.Item>
                            );
                        case 'upload':
                            return (
                                <Form.Item
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    rules={[{ required: field.required }]}
                                >
                                    <Upload {...getUploadProps(field.name)}>
                                        <Button icon={<UploadOutlined />} loading={loading}>
                                            {loading ? 'Uploading...' : `Upload ${field.label}`}
                                        </Button>
                                    </Upload>
                                </Form.Item>
                            );
                        default:
                            return null;
                    }
                })}

                <Button type="primary" htmlType="submit" loading={loading} block>
                    Create Assistant
                </Button>
            </Form>
        );
    };

    const handleUpload = async (info, fieldName) => {
        const { fileList } = info;
        const newFiles = fileList.filter(file => !file.knowledgeId);

        if (newFiles.length === 0) return;

        setLoading(true);
        try {
            const uploadPromises = newFiles.map(async (file) => {
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const encodedOriginalName = btoa(unescape(encodeURIComponent(file.name)))
                    .replace(/[/+]/g, '-')
                    .replace(/=+$/, '');

                const filePath = `${user.id}/knowledge/${Date.now()}_${encodedOriginalName}.${fileExtension}`;

                const { error: uploadError } = await supabase.storage
                    .from('knowledge')
                    .upload(filePath, file.originFileObj);

                if (uploadError) throw uploadError;

                const { data: processedData, error: processError } = await supabase.functions
                    .invoke('upload-knowledge', {
                        body: {
                            filePath,
                            fileName: file.name,
                            encodedName: encodedOriginalName,
                            fileType: file.type
                        }
                    });

                if (processError) throw processError;

                return {
                    ...file,
                    knowledgeId: processedData.knowledgeId,
                    originalName: file.name,
                    encodedName: encodedOriginalName
                };
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            const updatedFileList = fileList.map(existingFile => {
                const uploadedFile = uploadedFiles.find(f => f.uid === existingFile.uid);
                return uploadedFile || existingFile;
            });

            // 프롬프트 변수명에 맞춰 상태 업데이트
            setUploadedFilesByField(prev => ({
                ...prev,
                [fieldName]: updatedFileList
            }));

            // knowledge_ids를 프롬프트 변수명에 맞게 업데이트
            const allKnowledgeIds = updatedFileList
                .filter(f => f.knowledgeId)
                .map(f => f.knowledgeId);

            setUploadedKnowledgeIds(prev => ({
                ...prev,
                [`knowledge_${fieldName}_ids`]: allKnowledgeIds  // 예: knowledge_jd_ids
            }));

            message.success(`${uploadedFiles.length} files uploaded successfully`);

        } catch (error) {
            message.error(`File upload failed: ${error.message}`);
            setUploadedFilesByField(prev => ({
                ...prev,
                [fieldName]: prev[fieldName].filter(f => !newFiles.find(nf => nf.uid === f.uid))
            }));
        } finally {
            setLoading(false);
        }
    };


    const handleRemove = (fieldName, file) => {
        setUploadedFilesByField(prev => ({
            ...prev,
            [fieldName]: prev[fieldName].filter(f => f.uid !== file.uid)
        }));

        // 수정된 부분
        setUploadedKnowledgeIds(prev => ({
            ...prev,
            [`knowledge_${fieldName}_ids`]: prev[`knowledge_${fieldName}_ids`]?.filter(id => id !== file.knowledgeId) || []
        }));
    };

    // Upload 컴포넌트 설정도 수정
    const getUploadProps = (fieldName) => ({
        beforeUpload: (file) => {
            const allowedTypes = [
                'text/plain',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/pdf',
            ];
            // 파일 이름에 한글이 포함된 경우에도 처리할 수 있도록 수정
            const fileName = file.name;
            const fileExtension = fileName.split('.').pop().toLowerCase();

            // 확장자 기반으로 검사
            if (!['txt', 'docx', 'pdf'].includes(fileExtension)) {
                message.error(`${fileName}은(는) 지원되지 않는 파일 형식입니다. txt, docx, pdf 파일만 업로드해주세요.`);
                return Upload.LIST_IGNORE;
            }
            return false;
        },
        onChange: (info) => handleUpload(info, fieldName),
        multiple: true,
        showUploadList: {
            showRemoveIcon: true,
            removeIcon: <div style={{ color: '#ff4d4f' }}>삭제</div>,
        },
        fileList: uploadedFilesByField[fieldName] || [], // null check 추가
        onRemove: (file) => handleRemove(fieldName, file),
        accept: '.txt,.docx,.pdf'  // Add this line to show only these file types in file picker
    });

    const TemplateOption = ({ emoji, title, value, selected, onClick }) => (
        <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onClick(value)}
            style={{
                cursor: 'pointer',
                padding: '20px',
                margin: '10px',
                borderRadius: '12px',
                border: `2px solid ${selected ? '#1890ff' : '#e8e8e8'}`,
                backgroundColor: selected ? '#e6f7ff' : 'white',
                width: '200px',
                textAlign: 'center',
                boxShadow: selected ? '0 4px 12px rgba(24,144,255,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease'
            }}
        >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>{emoji}</div>
            <Typography.Text strong style={{ fontSize: '16px', color: selected ? '#1890ff' : '#333' }}>
                {title}
            </Typography.Text>
        </motion.div>
    );


    const TypeSelector = () => {
        const [searchText, setSearchText] = useState('');
        const [selectedCategory, setSelectedCategory] = useState('all');
        const [selectedTags, setSelectedTags] = useState([]);
        const [currentPage, setCurrentPage] = useState(1);
        const pageSize = 6;  // 한 페이지당 6개 템플릿 (2행 * 3열)

        const categories = [
            { key: 'all', label: 'All Templates' },
            { key: 'career', label: 'Career Development' },
            { key: 'business', label: 'Business' },
            { key: 'communication', label: 'Communication' },
            { key: 'custom', label: 'Custom' },
        ];

        const allTags = Array.from(new Set(templates.flatMap(t => t.tags)));

        const filteredTemplates = templates.filter(template => {
            const matchesSearch = template.title.toLowerCase().includes(searchText.toLowerCase()) ||
                template.description.toLowerCase().includes(searchText.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
            const matchesTags = selectedTags.length === 0 ||
                selectedTags.every(tag => template.tags.includes(tag));

            return matchesSearch && matchesCategory && matchesTags;
        });
        // 페이지네이션을 위한 데이터 슬라이싱
        const paginatedTemplates = filteredTemplates.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize
        );

        return (
            <div style={{ padding: '20px' }}>
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col span={24} md={8}>
                        <Input.Search
                            placeholder="Search templates..."
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={24} md={8}>
                        <Select
                            mode="multiple"
                            placeholder="Filter by tags"
                            style={{ width: '100%' }}
                            onChange={setSelectedTags}
                            options={allTags.map(tag => ({ label: tag, value: tag }))}
                        />
                    </Col>
                    <Col span={24} md={8}>
                        <Select
                            placeholder="Select category"
                            style={{ width: '100%' }}
                            onChange={setSelectedCategory}
                            defaultValue="all"
                        >
                            {categories.map(cat => (
                                <Select.Option key={cat.key} value={cat.key}>
                                    {cat.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                </Row>


                {/* Templates Grid - 수정된 버전 */}
                <Row gutter={[24, 24]} justify="start">
                    {paginatedTemplates.map((template) => (
                        <Col xs={24} sm={12} md={8} key={template.value}>
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Tooltip
                                    title={template.description}
                                    placement="bottom"
                                    overlayStyle={{ maxWidth: '300px' }}
                                >
                                    <Card
                                        hoverable
                                        onClick={() => setTemplateType(template.value)}
                                        style={{
                                            height: '100%',
                                            borderColor: template.highlight ? '#52c41a' : templateType === template.value ? '#1890ff' : '#e8e8e8',
                                        }}
                                        cover={
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '24px',
                                                background: template.highlight
                                                    ? 'linear-gradient(45deg, #f6ffed, #d9f7be)'
                                                    : 'linear-gradient(45deg, #f0f2f5, #e6f7ff)'
                                            }}>
                                                <span style={{ fontSize: '48px' }}>{template.emoji}</span>
                                                {template.highlight && (
                                                    <Tag color="success" style={{
                                                        position: 'absolute',
                                                        top: 10,
                                                        right: 10
                                                    }}>
                                                        Custom
                                                    </Tag>
                                                )}
                                            </div>
                                        }
                                        bodyStyle={{ padding: '12px' }}
                                    >
                                        <div style={{ minHeight: '120px' }}>
                                            <Typography.Title level={4} style={{
                                                marginBottom: '8px',
                                                fontSize: '16px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {template.title}
                                            </Typography.Title>
                                            <div style={{ marginBottom: '8px' }}>
                                                {template.tags.map(tag => (
                                                    <Tag key={tag} color="blue" style={{ marginBottom: '4px' }}>
                                                        {tag}
                                                    </Tag>
                                                ))}
                                            </div>
                                            <Row justify="space-between" align="middle">
                                                <Col>
                                                    <Rate disabled defaultValue={template.rating} style={{ fontSize: '12px' }} />
                                                </Col>
                                                <Col>
                                                    <span style={{ color: '#888', fontSize: '12px' }}>
                                                        👥 {template.usage_count}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Card>
                                </Tooltip>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* Pagination 추가 */}
                <Row justify="center" style={{ marginTop: '32px' }}>
                    <Pagination
                        current={currentPage}
                        total={filteredTemplates.length}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </Row>
            </div>
        );
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const selectedTemplate = templates.find(t => t.value === templateType);
            const { name, description, ...otherValues } = values;

            // config 객체 구성
            const config = {};

            selectedTemplate.required_fields?.fields?.forEach(field => {
                if (field.type === 'upload') {
                    // upload 타입의 경우 knowledge_[필드명]_ids 형식으로만 저장
                    const fileList = uploadedFilesByField[field.name] || [];
                    const knowledgeIds = fileList
                        .filter(file => file.knowledgeId)
                        .map(file => file.knowledgeId);

                    if (knowledgeIds.length > 0) {
                        config[`knowledge_${field.name}_ids`] = knowledgeIds;
                    }
                } else {
                    // 다른 타입의 필드는 그대로 저장
                    if (otherValues[field.name]) {
                        config[field.name] = otherValues[field.name];
                    }
                }
            });

            const { data: agent, error } = await supabase
                .from('agents')
                .insert([
                    {
                        name,
                        description,
                        agent_template_id: selectedTemplate.id,
                        config,
                        created_by: user.id,
                    }
                ])
                .select()
                .single();

            if (error) throw error;
            navigate('/agent/list')
            message.success('Assistant has been created');

        } catch (error) {
            console.error('Error:', error);
            message.error('Failed to create Assistant');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ margin: '0 auto', padding: '20px' }}>
            <Card hoverable style={{ borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <Button
                    type="link"
                    onClick={() => navigate('/agent/list')}
                    style={{
                        position: 'absolute',
                        left: 24,
                        top: 24
                    }}
                >
                    ← Back to List
                </Button>
                <Title level={2} style={{ textAlign: 'center', marginBottom: 24, color: '#1890ff' }}>
                    Personalize Meeting Assistant
                </Title>

                {!templateType && <TypeSelector />}
                {templateType && (
                    <DynamicForm
                        template={templates.find(t => t.value === templateType)}
                    />
                )}
            </Card>
        </div>
    );
};

export default AgentCreate;